import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { appendQuery } from '../../shared/utils/commenFunctions';

/** Canonical activity statuses: Draft / Active / In-Active */
export type ActivityStatus = 'draft' | 'active' | 'inactive';

export interface ActivityCreateRequest {
  activityName: string;
  code?: string;
  type?: string;
  description?: string;
  domainsOfWellness?: string[];
  limitedCapacity?: number | null;
  center?: string;
  activityImage?: string;
  isSpecial?: boolean;
  isSeries?: boolean;
  reoccurring?: string;
  timeMode?: 'all_day' | 'specific' | string;
  startTime?: string;
  endTime?: string;
  reoccurringDays?: string[];
  fromDate?: string;
  thruDate?: string;
  needsSpecialMenu?: boolean;
  offerHotMeal?: boolean;
  offerColdMeal?: boolean;
  /** When true, senior bookings appear in Staff Service Delivery. */
  serviceDeliveryNeeded?: boolean;
  specialMenuDays?: ActivitySpecialMenuDay[];
  status: ActivityStatus;
}

export interface ActivitySpecialMenuDay {
  date: string;
  day: string;
  hotMeal?: string | null;
  coldMeal?: string | null;
  specialEvents?: string | null;
}

export interface AdminActivityItem {
  id: string;
  activityName: string;
  activityImage?: string | null;
  code?: string | null;
  type?: string | null;
  groupName?: string | null;
  description?: string | null;
  domainsOfWellness?: string[];
  limitedCapacity?: number | null;
  center?: string | null;
  status: ActivityStatus | string;
  isSpecial?: boolean;
  isSeries?: boolean;
  reoccurring?: string | null;
  timeMode?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  reoccurringDays?: string[];
  fromDate?: string | null;
  thruDate?: string | null;
  needsSpecialMenu?: boolean;
  offerHotMeal?: boolean;
  offerColdMeal?: boolean;
  serviceDeliveryNeeded?: boolean;
  specialMenuDays?: ActivitySpecialMenuDay[];
  createdDate?: string;
}

export interface AdminActivityListResponse {
  statusCode: number;
  message: string;
  total: number;
  activities: AdminActivityItem[];
}

export interface AdminActivityResponse {
  statusCode: number;
  message: string;
  activity: AdminActivityItem;
}

export interface AdminActivityListParams {
  index?: number;
  limit?: number;
  searchText?: string;
  status?: string;
}

export interface ActivityBulkRowError {
  row: number;
  activityName?: string | null;
  message: string;
}

export interface ActivityBulkCreateResponse {
  statusCode: number;
  message: string;
  total: number;
  created: number;
  failed: number;
  activities: AdminActivityItem[];
  errors: ActivityBulkRowError[];
}

@Injectable({
  providedIn: 'root',
})
export class ActivityAdminService {
  private readonly apiService = inject(ApiService);

  listActivities(params: AdminActivityListParams = {}): Observable<AdminActivityListResponse> {
    const queryPayload: Record<string, unknown> = {
      index: params.index ?? 0,
      limit: params.limit ?? 50,
      searchText: params.searchText ?? '',
    };
    if (params.status) {
      queryPayload['status'] = params.status;
    }
    const url = appendQuery(API_ENDPOINTS.ADMIN.ACTIVITIES.LIST, queryPayload);
    return this.apiService.get<AdminActivityListResponse>(url);
  }

  getActivity(activityId: string): Observable<AdminActivityResponse> {
    return this.apiService.get<AdminActivityResponse>(
      API_ENDPOINTS.ADMIN.ACTIVITIES.DETAIL(activityId),
    );
  }

  createActivity(request: ActivityCreateRequest): Observable<AdminActivityResponse> {
    return this.apiService.post<AdminActivityResponse>(API_ENDPOINTS.ADMIN.ACTIVITIES.CREATE, request);
  }

  bulkCreateActivities(activities: ActivityCreateRequest[]): Observable<ActivityBulkCreateResponse> {
    return this.apiService.post<ActivityBulkCreateResponse>(API_ENDPOINTS.ADMIN.ACTIVITIES.BULK, {
      activities,
    });
  }

  updateActivity(activityId: string, request: ActivityCreateRequest): Observable<AdminActivityResponse> {
    return this.apiService.put<AdminActivityResponse>(
      API_ENDPOINTS.ADMIN.ACTIVITIES.UPDATE(activityId),
      request,
    );
  }

  updateActivityStatus(
    activityId: string,
    status: ActivityStatus,
  ): Observable<AdminActivityResponse> {
    return this.apiService.patch<AdminActivityResponse>(
      API_ENDPOINTS.ADMIN.ACTIVITIES.STATUS(activityId),
      { status },
    );
  }
}
